// BHP-1115: edit the hostname and the labels the customer owns.
//
// Both go to Hetzner (BHP-1104), not to our own columns — the `vps` table is a
// projection, so a local write would be undone by the next sync.
//
// A dialog rather than a block on the page: the detail card is a dense grid of
// facts, and a form that unfolds inside it competes with them for the same
// space. Follows RegisterDomainModal, so the keyboard contract (focus in,
// Tab cycles, Escape closes, focus back out) comes from useDialogA11y.
//
// The platform's own labels are not shown at all. `customerUuid` is an internal
// identifier and the rest are our bookkeeping; the customer never set them and
// cannot change them (BHP-1114), so putting them on screen is noise that reads
// like something they should understand.
import { useState } from 'react';
import { toFormErrors } from '../../../shared/http/errorMessage';
import { useDialogA11y } from '../../../shared/ui/useDialogA11y';
import type { VpsSummary } from '../domain/vpsSummary';
import {
  buildVpsEdit,
  canAddLabelRow,
  editableLabels,
  isEmptyEdit,
  type LabelRow,
  labelRowErrors
} from '../domain/vpsLabels';
import { useVpsEdit } from '../application/vpsEditMutations';

// Same shapes as DnsRecordForm, which is the form vocabulary this app already
// uses. Repeated rather than shared for now: two files do not make a module,
// and hoisting them is a change to that form as much as to this one.
const FIELD =
  'w-full rounded-md border border-solid border-slate-300 px-3 py-1.5 text-sm text-slate-800 focus:border-teal-500 focus:outline-none';
const LABEL = 'mb-1 block text-xs font-medium text-slate-600';

/** Fields the form renders, so a violation can be bound to its input. */
const FORM_FIELDS = ['hostname'] as const;

export default function VpsEditModal({
  server,
  onClose
}: {
  server: VpsSummary;
  onClose: () => void;
}): JSX.Element {
  const edit = useVpsEdit(server.uuid);
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);

  // Seeded from the server once, on mount. The dialog is unmounted on close, so
  // reopening starts from what the API returned rather than from a stale edit.
  const [hostname, setHostname] = useState(server.hostname);
  const [rows, setRows] = useState<LabelRow[]>(() =>
    editableLabels(server.labels)
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const updateRow = (index: number, row: LabelRow): void => {
    setRows((current) =>
      current.map((existing, i) => (i === index ? row : existing))
    );
  };

  const rowErrors = labelRowErrors(rows);
  const hasRowErrors = Object.keys(rowErrors).length > 0;

  const submit = (): void => {
    setFieldErrors({});
    setFormError(null);

    if (hasRowErrors) {
      // Refuse rather than drop: buildVpsEdit() would leave a reserved or
      // duplicate key out, the save would report success, and the label the
      // user typed would simply not be there.
      setFormError('Fix the labels marked below first.');

      return;
    }

    const payload = buildVpsEdit(server, hostname, rows);

    if (isEmptyEdit(payload)) {
      // Nothing changed. Posting anyway would burn a write and a Hetzner round
      // trip to end up exactly where we are.
      onClose();
      return;
    }

    edit.save(payload).then(onClose, (error: unknown) => {
      const { fields, message } = toFormErrors(
        error,
        'The change could not be saved; nothing was modified.',
        FORM_FIELDS
      );
      setFieldErrors(fields);
      setFormError(message);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vps-edit-title"
        className="w-full max-w-lg overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-xl"
      >
        <div className="border-0 bg-slate-50 px-5 py-3">
          <h3
            id="vps-edit-title"
            className="text-base font-semibold text-slate-900"
          >
            Edit server
          </h3>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="space-y-4 px-5 py-4">
            <div>
              <label className={LABEL} htmlFor="vps-hostname">
                Hostname
              </label>
              <input
                id="vps-hostname"
                value={hostname}
                onChange={(event) => {
                  setHostname(event.target.value);
                }}
                className={FIELD}
              />
              {fieldErrors.hostname === undefined ? null : (
                <p className="mt-1 text-xs text-rose-600">
                  {fieldErrors.hostname}
                </p>
              )}
            </div>

            <div>
              <p className={LABEL}>Labels</p>

              {rows.length === 0 ? (
                <p className="m-0 text-xs text-slate-400">
                  No labels yet. Add one to tag this server.
                </p>
              ) : (
                <div className="space-y-2">
                  {rows.map((row, index) => (
                    // Index as key: rows have no stable id and the key is
                    // editable, so keying on it would remount the input on
                    // every keystroke.
                    // eslint-disable-next-line react/no-array-index-key
                    <div
                      key={index}
                      className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
                    >
                      <input
                        aria-label={`Label ${index + 1} name`}
                        placeholder="Name"
                        value={row.key}
                        onChange={(event) => {
                          updateRow(index, { ...row, key: event.target.value });
                        }}
                        className={FIELD}
                      />
                      <input
                        aria-label={`Label ${index + 1} value`}
                        placeholder="Value"
                        value={row.value}
                        onChange={(event) => {
                          updateRow(index, {
                            ...row,
                            value: event.target.value
                          });
                        }}
                        className={FIELD}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setRows((current) =>
                            current.filter((_, i) => i !== index)
                          );
                        }}
                        className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Remove
                      </button>
                      {rowErrors[index] === undefined ? null : (
                        <p className="m-0 text-xs text-rose-600 sm:col-span-3">
                          {rowErrors[index]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                // Disabled while a row has no name yet: clicking on would stack
                // blank rows that go nowhere.
                disabled={!canAddLabelRow(rows)}
                onClick={() => {
                  setRows((current) => [...current, { key: '', value: '' }]);
                }}
                className="mt-2 rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Add label
              </button>
            </div>

            {formError === null ? null : (
              <p
                role="alert"
                className="rounded-md border border-solid border-rose-200 bg-rose-50 p-2 text-sm text-rose-700"
              >
                {formError}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-0 bg-slate-50 px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={edit.isPending}
              className="rounded-md border border-solid border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={edit.isPending || hasRowErrors}
              className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {edit.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
